/**
 * Per-route `<head>` manager (#103, FR-15.3) — a tiny hook, not a framework (D103.6).
 *
 * On mount (and whenever the head model changes) it imperatively upserts the route's
 * `document.title`, `<meta>` set (description + Open Graph + Twitter card), the
 * `<link rel="canonical">`, and — when supplied — a `<script type="application/ld+json">`.
 * Tags it manages are marked with `data-managed-head` so re-applying is idempotent and a
 * later route can update rather than duplicate them. The build-time prerender bakes the
 * landing's head into the served HTML for crawlers (FR-15.2); this hook is what keeps the
 * head correct for client-side route changes and for the app/legal routes that stay SPA.
 */

import { useEffect } from 'react';
import { canonicalUrl, metaTagsFor, type DocumentHead } from './head-tags';

const MANAGED = 'data-managed-head';

/** Upsert a `<meta>` matched by its `name`/`property`, tagging it as managed. */
function upsertMeta(key: 'name' | 'property', keyValue: string, content: string): void {
  const selector = `meta[${key}="${keyValue}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(key, keyValue);
    el.setAttribute(MANAGED, '');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/** Upsert the single `<link rel="canonical">`. */
function upsertCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    el.setAttribute(MANAGED, '');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/** Replace the managed JSON-LD `<script>` (or remove it when the route has none). */
function upsertJsonLd(jsonLd: DocumentHead['jsonLd']): void {
  const existing = document.head.querySelector(
    `script[type="application/ld+json"][${MANAGED}]`,
  );
  if (!jsonLd) {
    existing?.remove();
    return;
  }
  const el = existing ?? document.createElement('script');
  el.setAttribute('type', 'application/ld+json');
  el.setAttribute(MANAGED, '');
  el.textContent = JSON.stringify(jsonLd);
  if (!existing) document.head.appendChild(el);
}

/**
 * Remove managed `<meta>` tags that the incoming head no longer wants (#241). Without
 * this, upsert-only application leaves stale tags behind on a client-side route change —
 * e.g. navigating from a `noindex` route (`/profile`) to an indexable one would strip the
 * page's own head but keep the `robots=noindex` meta, mis-flagging it for JS crawlers;
 * likewise `og:image`/`twitter:image` would linger when moving to an imageless route. We
 * only prune tags WE manage (marked `data-managed-head`), so the static shell head is
 * untouched.
 */
function pruneManagedMeta(desired: ReturnType<typeof metaTagsFor>): void {
  const wanted = new Set(
    desired.map((t) => (t.name ? `name:${t.name}` : `property:${t.property!}`)),
  );
  for (const el of document.head.querySelectorAll<HTMLMetaElement>(`meta[${MANAGED}]`)) {
    const key = el.getAttribute('name')
      ? `name:${el.getAttribute('name')!}`
      : el.getAttribute('property')
        ? `property:${el.getAttribute('property')!}`
        : null;
    if (key && !wanted.has(key)) el.remove();
  }
}

/** Apply a head model to the live document (exported for direct/testing use). */
export function applyDocumentHead(head: DocumentHead): void {
  document.title = head.title;
  const tags = metaTagsFor(head);
  for (const tag of tags) {
    if (tag.name) upsertMeta('name', tag.name, tag.content);
    else if (tag.property) upsertMeta('property', tag.property, tag.content);
  }
  // Drop any managed meta the new route no longer wants (stale robots/image on nav).
  pruneManagedMeta(tags);
  upsertCanonical(canonicalUrl(head.canonicalPath));
  upsertJsonLd(head.jsonLd);
}

/** React hook wrapper around {@link applyDocumentHead}. */
export function useDocumentHead(head: DocumentHead): void {
  useEffect(() => {
    applyDocumentHead(head);
    // Re-run when any field changes; the model is a stable module constant for most
    // routes, so this is effectively mount-only there.
  }, [head]);
}
